import React from 'react';
import { MapPin, Clock, Phone, Mail, Navigation } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { clinicInfo } from '../data/mockData';

const LocationsPage = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an appointment`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Our Locations
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Conveniently located in Bellandur to serve the local community. 
            Choose the clinic nearest to you.
          </p>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            {clinicInfo.locations.map((location) => (
              <Card key={location.id} className="border-2 border-border hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-primary mb-6 text-center">
                    {location.name}
                  </h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-muted-foreground">Address</p>
                        <p className="text-muted-foreground">{location.address}</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">{location.landmark}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-muted-foreground">Hours</p>
                        <p className="text-muted-foreground">{location.hours}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-muted-foreground">Phone</p>
                        <a href={`tel:${clinicInfo.phone}`} className="text-primary hover:underline">
                          {clinicInfo.phone}
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium text-muted-foreground">Email</p>
                        <a href={`mailto:${clinicInfo.email}`} className="text-primary hover:underline">
                          {clinicInfo.email}
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Button 
                      onClick={() => window.open(location.mapLink, '_blank')}
                      className="w-full"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Get Directions
                    </Button>
                    <Button 
                      onClick={handleWhatsApp}
                      variant="outline"
                      className="w-full"
                    >
                      Book Appointment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Easy to Find
            </h2>
            <p className="text-lg text-muted-foreground">
              Our clinic is easily accessible with ample parking available
            </p>
          </div>
          <div className="bg-gray-200 rounded-lg h-96 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">
                Click "Get Directions" above to view interactive map
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Visit Us Today
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Walk-ins welcome, but appointments are recommended to ensure availability.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              className="text-white text-lg px-8 py-6 transition-all duration-300"
            >
              Book on WhatsApp
            </Button>
            <Button 
              onClick={() => window.location.href = `tel:${clinicInfo.phone}`}
              size="lg"
              variant="outline"
              className="text-white text-lg px-8 py-6 transition-all duration-300"
            >
              Call Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationsPage;
